import * as React from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

export function BottomSheet({
  open,
  onClose,
  title,
  children,
  className,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  className?: string;
}) {
  const { t } = useTranslation();
  // Motion анимирует инлайн-трансформом, CSS-медиазапрос его не остановит — нужен хук.
  // Заезжающий снизу шит это как раз то движение, из-за которого включают reduce.
  const reduceMotion = useReducedMotion();

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={reduceMotion ? { opacity: 0, transition: { duration: 0 } } : { opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-end bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={reduceMotion ? false : { y: '100%' }}
            animate={{ y: 0 }}
            exit={reduceMotion ? { y: '100%', transition: { duration: 0 } } : { y: '100%' }}
            transition={reduceMotion ? { duration: 0 } : { type: 'spring', damping: 30, stiffness: 300 }}
            className={cn(
              "w-full max-w-md mx-auto rounded-t-[28px] px-6 pt-3 border-t border-white/10 shadow-[0_-20px_50px_rgba(0,0,0,0.6)] flex flex-col",
              className,
            )}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--calm-sheet)',
              maxHeight: '90dvh',
              paddingBottom: 'calc(env(safe-area-inset-bottom) + 1.5rem)',
            }}
          >
            <div className="h-1 w-[38px] rounded-full bg-white/18 mx-auto shrink-0" aria-hidden />
            {title && (
              <div className="flex items-center justify-between pt-3 pb-2 shrink-0">
                <h3 className="text-xl font-bold">{title}</h3>
                {/* Кружок остаётся 30px, но хит-область растянута псевдоэлементом до 44px
                    (WCAG 2.5.5): пальцем в 30px попадают не с первого раза. */}
                <button
                  type="button"
                  onClick={onClose}
                  aria-label={t('Close')}
                  className="relative w-[30px] h-[30px] rounded-full bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors text-[#9a9ca3] after:absolute after:-inset-[7px] after:content-['']"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            {/* Скроллируемый контент — иначе длинный список (например 9 тарифов в gift)
                уходит за экран без возможности листать. overscroll-contain: докрутив
                список до конца, палец не должен начать тянуть страницу под шитом. */}
            <div className="flex-1 overflow-y-auto overscroll-contain space-y-5 -mx-2 px-2 pb-2">
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'OK',
  cancelLabel,
  destructive = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}) {
  const { t } = useTranslation();
  // Шторка закрывается сразу, но ещё уезжает вниз, и кнопка под пальцем живая: второй
  // щелчок двойного клика запускал действие повторно (заморозка следом размораживала).
  // Одно открытие — одно подтверждение.
  const firedRef = React.useRef(false);
  React.useEffect(() => {
    if (open) firedRef.current = false;
  }, [open]);
  return (
    <BottomSheet open={open} onClose={onClose} title={title}>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onClose}
          className="bg-muted text-foreground py-3 rounded-xl text-sm font-bold active:scale-[0.98] transition-transform"
        >
          {cancelLabel ?? t('Cancel')}
        </button>
        <button
          type="button"
          onClick={() => {
            if (firedRef.current) return;
            firedRef.current = true;
            onConfirm();
            onClose();
          }}
          className={cn(
            "py-3 rounded-xl text-sm font-bold active:scale-[0.98] transition-transform",
            destructive ? "calm-cta-danger" : "calm-cta"
          )}
        >
          {confirmLabel}
        </button>
      </div>
    </BottomSheet>
  );
}
